import { ref, get, push, set } from "firebase/database";
import { db } from "../firebaseConfig";
import { AuditLogEntry, AuditLogFilter } from "../../models/auditLog";

const auditLogsRef = ref(db, "auditLogs");

const normalizeLogs = (data: Record<string, any>): AuditLogEntry[] => {
	return Object.entries(data).map(([id, value]) => ({
		id,
		...(value as Omit<AuditLogEntry, "id">)
	}));
};

const removeUndefinedValues = (value: unknown): unknown => {
	if (value === undefined) {
		return undefined;
	}

	if (Array.isArray(value)) {
		return value.map((item) => {
			const cleaned = removeUndefinedValues(item);
			return cleaned === undefined ? null : cleaned;
		});
	}

	if (value && typeof value === "object" && !(value instanceof Date)) {
		return Object.entries(value as Record<string, unknown>).reduce(
			(acc: Record<string, unknown>, [key, nestedValue]) => {
				const cleaned = removeUndefinedValues(nestedValue);
				if (cleaned !== undefined) {
					acc[key] = cleaned;
				}
				return acc;
			},
			{}
		);
	}

	return value;
};

export const saveAuditLog = async (auditLog: Omit<AuditLogEntry, "id">) => {
	try {
		const auditRef = push(auditLogsRef);
		const sanitizedLog = removeUndefinedValues(auditLog) as Omit<
			AuditLogEntry,
			"id"
		>;
		await set(auditRef, sanitizedLog);
		// eslint-disable-next-line no-console
		console.log(
			"Saved audit log",
			auditRef.key,
			auditLog.entityType,
			auditLog.entityId
		);
		return auditRef.key as string;
	} catch (err) {
		// eslint-disable-next-line no-console
		console.error("Failed to save audit log:", err, auditLog);
		throw err;
	}
};

export const getAuditLogs = async (): Promise<AuditLogEntry[]> => {
	const snapshot = await get(auditLogsRef);
	if (!snapshot.exists()) {
		return [];
	}

	const logs = normalizeLogs(snapshot.val());
	return logs.sort(
		(a, b) =>
			new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
	);
};

export const queryAuditLogs = async (
	filter: AuditLogFilter
): Promise<AuditLogEntry[]> => {
	const snapshot = await get(auditLogsRef);
	if (!snapshot.exists()) {
		return [];
	}

	const logs = normalizeLogs(snapshot.val());
	return logs
		.filter((entry) => {
			// Extract date from ISO timestamp (YYYY-MM-DD format)
			const entryDate = entry.timestamp.substring(0, 10);
			return entryDate >= filter.startDate && entryDate <= filter.endDate;
		})
		.filter((entry) => {
			if (filter.entityType && filter.entityType !== "All") {
				return entry.entityType === filter.entityType;
			}
			return true;
		})
		.filter((entry) => {
			if (filter.operations && filter.operations.length > 0) {
				return filter.operations.includes(entry.operation);
			}
			return true;
		})
		.filter((entry) => {
			if (filter.username) {
				return entry.username
					.toLowerCase()
					.includes(filter.username.toLowerCase());
			}
			return true;
		})
		.sort(
			(a, b) =>
				new Date(b.timestamp).getTime() -
				new Date(a.timestamp).getTime()
		);
};

export const getAuditLogsByItemCode = async (
	itemCode: string
): Promise<AuditLogEntry[]> => {
	const snapshot = await get(auditLogsRef);
	if (!snapshot.exists()) {
		return [];
	}

	const logs = normalizeLogs(snapshot.val());
	return logs
		.filter((entry) => {
			const metadata = entry.metadata ?? {};
			if (metadata.itemCode === itemCode) {
				return true;
			}

			const soldItems = metadata.soldItems as Array<
				{ itemCode?: string; itemKey?: string } | undefined
			>;
			if (Array.isArray(soldItems)) {
				if (
					soldItems.some(
						(item) =>
							item?.itemCode === itemCode ||
							item?.itemKey === itemCode
					)
				) {
					return true;
				}
			}

			const returnedItems = metadata.returnedItems as Array<
				{ itemCode?: string; itemKey?: string } | undefined
			>;
			if (Array.isArray(returnedItems)) {
				if (
					returnedItems.some(
						(item) =>
							item?.itemCode === itemCode ||
							item?.itemKey === itemCode
					)
				) {
					return true;
				}
			}

			return false;
		})
		.sort(
			(a, b) =>
				new Date(b.timestamp).getTime() -
				new Date(a.timestamp).getTime()
		);
};
